import type { ReactNode } from 'react'
import { useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import { X } from 'lucide-react'
import { cn } from '@/lib/cn'

/**
 * Right-side slide-over for detail views. Neutral chrome like Modal; the
 * behavior stack (Escape, scrim, body scroll lock, imperative focus) mirrors
 * PortalDrawer, which is hard-bound to portal navigation and cannot be
 * reused directly. Full-width below `sm`, so it becomes a takeover on phones.
 */
export default function Drawer({
  open,
  onClose,
  title,
  children,
  footer,
  width = 'max-w-md',
}: {
  open: boolean
  onClose: () => void
  title?: ReactNode
  children: ReactNode
  footer?: ReactNode
  width?: string
}) {
  const closeRef = useRef<HTMLButtonElement>(null)
  const reduce = useReducedMotion()

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])

  useEffect(() => {
    if (!open) return
    const previous = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = previous
    }
  }, [open])

  // autoFocus is unreliable inside a portal plus AnimatePresence tree.
  useEffect(() => {
    if (open) closeRef.current?.focus()
  }, [open])

  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-50"
          initial={reduce ? false : { opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: reduce ? 0 : 0.18, ease: 'easeOut' }}
        >
          <div className="absolute inset-0 bg-foreground/50 backdrop-blur-sm" onMouseDown={onClose} />

          <motion.div
            className={cn('absolute inset-y-0 right-0 flex w-full flex-col bg-background shadow-xl', width)}
            initial={reduce ? false : { x: 24 }}
            animate={{ x: 0 }}
            exit={reduce ? undefined : { x: 24 }}
            transition={{ duration: reduce ? 0 : 0.18, ease: 'easeOut' }}
          >
            <div className="flex items-center justify-between border-b border-foreground/10 px-5 py-4">
              <h3 className="text-base font-medium text-foreground">{title}</h3>
              <button
                type="button"
                ref={closeRef}
                onClick={onClose}
                aria-label="Close"
                className="-m-2 flex size-10 items-center justify-center rounded-lg text-muted hover:bg-foreground/5 hover:text-ink"
              >
                <X className="size-5" />
              </button>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">{children}</div>
            {footer && <div className="flex flex-wrap justify-end gap-2 border-t border-line px-5 py-3">{footer}</div>}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body,
  )
}
